import type { FormEvent} from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";

import { useGames, useSetGameArchived } from "@app/api/games";
import type { GameStatus } from "@app/api/types";
import { extractErrorMessage } from "@app/api/client";
import { ErrorMessage, Loading } from "@app/components/Feedback";
import { ROWS_PER_PAGE_OPTIONS } from "@app/constants";

const STATUS_COLOR: Record<GameStatus, "success" | "warning" | "default"> = {
  published: "success",
  draft: "warning",
  archived: "default"
};

// Player URL template with a ":uuid" placeholder (same convention as the dashboard).
const PLAYER_URL = import.meta.env.VITE_PLAYER_URL || "https://player.theufolab.com/play/:uuid";
const playerLink = (uuid: string): string => PLAYER_URL.replace(":uuid", uuid);

export default function GamesPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<GameStatus | "">("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading, error } = useGames({
    offset: page * rowsPerPage,
    limit: rowsPerPage,
    q: q || undefined,
    status: status || undefined
  });
  const setArchived = useSetGameArchived();

  const onSearch = (event: FormEvent): void => {
    event.preventDefault();
    setQ(searchInput.trim());
    setPage(0);
  };

  const onToggle = async (id: number, archived: boolean, name: string): Promise<void> => {
    const verb = archived ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${verb} "${name}"?`)) return;
    try {
      await setArchived.mutateAsync({ id, archived });
      enqueueSnackbar(`Game ${verb}d`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err, `Failed to ${verb} game`), { variant: "error" });
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Games
      </Typography>

      <Stack direction="row" spacing={1} component="form" onSubmit={onSearch} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ width: 280 }}
        />
        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as GameStatus | "");
            setPage(0);
          }}
          sx={{ width: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="published">Published</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Stack>

      {isLoading && <Loading />}
      {error && <ErrorMessage error={error} />}

      {data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((game) => {
                const archived = game.status === "archived";
                return (
                  <TableRow key={game.id} hover>
                    <TableCell>
                      {game.status === "published" ? (
                        <Link href={playerLink(game.uuid)} target="_blank" rel="noopener noreferrer">
                          {game.name ?? `Game #${game.id}`}
                        </Link>
                      ) : (
                        game.name ?? `Game #${game.id}`
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={game.status} color={STATUS_COLOR[game.status]} />
                    </TableCell>
                    <TableCell>
                      {game.ownerId ? (
                        <Link component="button" onClick={() => navigate(`/users/${game.ownerId}`)}>
                          {game.ownerEmail ?? game.ownerId}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{game.organizationName ?? "—"}</TableCell>
                    <TableCell>{new Date(game.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      {game.status === "published" && (
                        <Tooltip title="Open game in player">
                          <IconButton
                            size="small"
                            component="a"
                            href={playerLink(game.uuid)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Button
                        size="small"
                        color={archived ? "success" : "error"}
                        onClick={() => onToggle(game.id, !archived, game.name ?? `Game #${game.id}`)}
                        disabled={setArchived.isPending}
                      >
                        {archived ? "Enable" : "Disable"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No games
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total ?? -1}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}
    </Box>
  );
}
